import React, { useRef, useEffect, useState } from 'react';
import { Formik, useFormikContext } from 'formik';
import { Box, Button, Link, Stack, Text } from '@chakra-ui/core';
import { useSelector } from 'react-redux';
import {
  TextInput,
  RadioGroup,
  RadioInput,
  CheckboxGroupInput,
  PhoneInput,
} from '../Form/FormInputs';
import { PrimaryButton, SecondaryButton } from '../Buttons';
import { Form, FormActions, FormSection } from '../Form/Form';
import { history } from '../../helpers/history';
import { SelectInput } from '../Form/FormSelectInput';
import Can, { checkPermissions } from '../../../roleBasedAccess/Can';
import { companiesAPI } from '../../api/companies';
import FileUploadError from '../FileUploadError';
import {
  getUploadError,
  MAX_UPLOAD_SIZE_BYTES,
  MAX_UPLOAD_SIZE_MB,
  UPLOAD_SIZE_ERROR,
} from '../../helpers/fileUpload';
import { companyLegalDocumentUrl } from '../../helpers/documentFiles';

const LegalDocumentInput = ({
  companyId,
  documentType,
  label,
  filename,
  downloadLink,
  contentType,
  required,
}) => {
  const { values, setFieldValue, errors } = useFormikContext();
  const fileInputRef = useRef();
  const field =
    documentType === 'civil_liability_insurance'
      ? 'civilLiabilityInsuranceFile'
      : 'decennialGuaranteeFile';
  const [document, setDocument] = useState({ filename, downloadLink, contentType });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const upload = event => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      setError(UPLOAD_SIZE_ERROR);
      return;
    }

    setError(null);
    if (!companyId) {
      setFieldValue(field, file);
      setDocument({ filename: file.name });
      return;
    }
    setPending(true);
    companiesAPI
      .uploadLegalDocument(companyId, documentType, file)
      .then(({ company }) => {
        const prefix =
          documentType === 'civil_liability_insurance'
            ? 'civilLiabilityInsurance'
            : 'decennialGuarantee';
        setDocument({
          filename: company[`${prefix}Filename`],
          downloadLink: company[`${prefix}DownloadLink`],
          contentType: company[`${prefix}ContentType`],
        });
      })
      .catch(uploadFailure => setError(getUploadError(uploadFailure)))
      .finally(() => setPending(false));
  };

  if (!companyId && !required) {
    return <Text fontSize="sm">Enregistrez l’entreprise pour ajouter ses justificatifs.</Text>;
  }

  return (
    <Box>
      <Text as="label" htmlFor={`company-${documentType}`} d="block" fontWeight="semibold" mb={2}>
        {label}
        {required ? ' *' : ''}
      </Text>
      {document.downloadLink && (
        <Link
          href={companyLegalDocumentUrl(
            companyId,
            documentType,
            document.downloadLink,
            document.contentType,
          )}
          target="_blank"
          color="coral.900"
          mr={4}
        >
          {document.filename}
        </Link>
      )}
      <input
        id={`company-${documentType}`}
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        hidden
        onChange={upload}
      />
      <Button
        type="button"
        variantColor="neutral"
        onClick={() => fileInputRef.current.click()}
        leftIcon="upload"
        cursor="pointer"
        isDisabled={pending}
        isLoading={pending}
        loadingText="Envoi en cours"
      >
        {document.filename ? 'Remplacer' : 'Ajouter un document'}
      </Button>
      <Text fontSize="xs" color="semiDarkGray.600" mt={2}>
        {`PDF, JPG ou PNG · ${MAX_UPLOAD_SIZE_MB} Mo maximum par fichier`}
      </Text>
      {!companyId && values[field] && (
        <Text fontSize="sm" mt={2}>
          {values[field].name}
        </Text>
      )}
      <FileUploadError error={error || errors[field]} mb={4} />
    </Box>
  );
};

export const CompanyForm = ({
  initialValues,
  onSubmit,
  pending,
  error,
  mode,
  departments,
  qualifications,
}) => {
  const formikRef = useRef();
  const { profile, user } = useSelector(state => state.authentication);
  const currentUser = Object.keys(profile || {}).length === 0 ? user : profile;
  const isPartner = ['référent entreprise', 'coordinateur entreprises'].includes(currentUser?.role);
  const isReferent = currentUser?.role === 'référent entreprise';
  const canUpdateAllFields =
    mode === 'create' ||
    checkPermissions(currentUser?.role, 'companies:update_all_fields', {
      user: currentUser,
    });
  const phoneRequired =
    mode === 'create' || initialValues.source === 'HB' || initialValues.requiresLegalIdentity;
  const legalIdentityRequired =
    (mode === 'create' && isPartner) || (mode === 'edit' && canUpdateAllFields);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.setErrors(error?.errors || {});
    }
  }, [error]);

  const filterDepartments = inputValue => {
    if (!inputValue) return [];
    return departments
      ?.filter(i => i.label.toLowerCase().includes(inputValue.toLowerCase()))
      .sort(
        (a, b) =>
          a.label.toLowerCase().indexOf(inputValue.toLowerCase()) -
          b.label.toLowerCase().indexOf(inputValue.toLowerCase()),
      )
      .slice(0, 20);
  };

  const loadOptions = (inputValue, callback) => {
    setTimeout(() => {
      callback(filterDepartments(inputValue));
    }, 1000);
  };

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={mode === 'edit'}
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting, setTouched, setErrors }) => {
        setSubmitting(false);
        setTouched({});
        if (mode === 'create' && isPartner) {
          const missing = [
            'kbis',
            'siren',
            'civilLiabilityInsuranceFile',
            'decennialGuaranteeFile',
          ].reduce(
            (errors, key) =>
              values[key] ? errors : { ...errors, [key]: ['Ce champ est obligatoire.'] },
            {},
          );
          if (Object.keys(missing).length) {
            setErrors(missing);
            return;
          }
        }
        onSubmit(values);
      }}
    >
      {formik => (
        <Form onSubmit={formik.handleSubmit}>
          <Stack spacing={16}>
            <FormSection title="Informations de contact">
              <TextInput
                label="Nom de l'entreprise"
                type="text"
                id="name"
                name="name"
                placeholder="Ma société"
                disabled={pending}
                readOnly={!canUpdateAllFields}
                requiredInput
              />
              <TextInput
                label="Mail"
                type="email"
                id="email"
                name="email"
                placeholder="contact.societe@gmail.com"
                disabled={pending}
                requiredInput
              />
              <PhoneInput
                label="Téléphone"
                id="phoneNumber"
                name="phoneNumber"
                placeholder="0602050842"
                disabled={pending}
                requiredInput={phoneRequired}
              />
              <TextInput
                label="Kbis"
                type="text"
                id="kbis"
                name="kbis"
                placeholder="Référence Kbis"
                disabled={pending}
                requiredInput={legalIdentityRequired}
              />
              <TextInput
                label="SIREN"
                type="text"
                id="siren"
                name="siren"
                placeholder="Numéro SIREN"
                disabled={pending}
                readOnly={!canUpdateAllFields}
                requiredInput={legalIdentityRequired}
              />
              <SelectInput
                mt="2.8rem"
                placeholder="Sélectionner les départements"
                label="Départements"
                name="departments"
                id="departments"
                isMulti
                isAsync
                defaultOptions
                cacheOptions
                noOptionsMessage={({ inputValue }) => {
                  if (inputValue) return 'Aucun code postal ne correspond à cette recherche';
                  return 'Taper pour rechercher';
                }}
                loadingMessage={() => 'Recherche en cours...'}
                loadOptions={loadOptions}
                readOnly={!canUpdateAllFields}
                isDisabled={formik.isSubmitting || !canUpdateAllFields}
              />
            </FormSection>
            <FormSection title="Pièces justificatives">
              <FileUploadError error={formik.errors?.documents} mb={4} />
              <LegalDocumentInput
                companyId={initialValues.id}
                required={mode === 'create' && isPartner}
                documentType="civil_liability_insurance"
                label="Assurance responsabilité civile"
                filename={initialValues.civilLiabilityInsuranceFilename}
                downloadLink={initialValues.civilLiabilityInsuranceDownloadLink}
                contentType={initialValues.civilLiabilityInsuranceContentType}
              />
              <LegalDocumentInput
                companyId={initialValues.id}
                required={mode === 'create' && isPartner}
                documentType="decennial_guarantee"
                label="Garantie décennale"
                filename={initialValues.decennialGuaranteeFilename}
                downloadLink={initialValues.decennialGuaranteeDownloadLink}
                contentType={initialValues.decennialGuaranteeContentType}
              />
            </FormSection>
            <FormSection title="Certification">
              <RadioGroup
                flexDir="column"
                name="certification"
                legend="L'entreprise dispose-t-elle des certifications requises par Action Logement ?"
                readOnly={!canUpdateAllFields}
              >
                <RadioInput value="true" disabled={pending}>
                  Oui
                </RadioInput>

                <RadioInput value="false" disabled={pending}>
                  Non
                </RadioInput>
              </RadioGroup>
            </FormSection>
            {!isReferent && (
              <FormSection title="Autorisations">
                <RadioGroup
                  name="hasAccessToProjectPlace"
                  legend="Accès à la Project Place"
                  readOnly={!canUpdateAllFields}
                >
                  <RadioInput value="true" disabled={pending}>
                    Oui
                  </RadioInput>
                  <RadioInput
                    value="false"
                    disabled={pending}
                    postOnChange={() =>
                      formik.setFieldValue('hasPriorityAccessToProjectPlace', 'false')
                    }
                  >
                    Non
                  </RadioInput>
                </RadioGroup>
                <Can
                  perform="companies:edit_priority_access"
                  yes={() => (
                    <RadioGroup
                      name="hasPriorityAccessToProjectPlace"
                      legend="Accès prioritaire à la Project Place"
                      disabled={pending || formik.values.hasAccessToProjectPlace !== 'true'}
                    >
                      <RadioInput value="true">Oui</RadioInput>
                      <RadioInput value="false">Non</RadioInput>
                    </RadioGroup>
                  )}
                />
                <RadioGroup
                  name="hasNotifications"
                  legend="Notifications mail"
                  readOnly={!canUpdateAllFields}
                  fieldsetStyle={{
                    mb: 0,
                  }}
                >
                  <RadioInput value="true" disabled={pending}>
                    Oui
                  </RadioInput>
                  <RadioInput value="false" disabled={pending}>
                    Non
                  </RadioInput>
                </RadioGroup>
                <RadioGroup
                  name="hasSmsNotifications"
                  legend="Notifications SMS"
                  readOnly={!canUpdateAllFields}
                  fieldsetStyle={{
                    mb: 0,
                  }}
                >
                  <RadioInput value="true" disabled={pending}>
                    Oui
                  </RadioInput>
                  <RadioInput value="false" disabled={pending}>
                    Non
                  </RadioInput>
                </RadioGroup>
              </FormSection>
            )}
            <Can
              perform="companies:edit_qualifications"
              yes={() => (
                <FormSection title="Qualifications">
                  {formik.values && formik.values.qualificationIds
                    ? qualifications.map(qualification => {
                        const isChecked = formik.values.qualificationIds.includes(qualification.id);

                        return (
                          <CheckboxGroupInput
                            key={`qualification_${qualification.id}`}
                            name="qualificationIds"
                            value={qualification.id}
                            checked={isChecked}
                            onChange={e => {
                              const { value } = e.target;
                              const id = Number(value);
                              const currentIds = formik.values.qualificationIds;
                              const updatedQualifications = [...currentIds];

                              if (!updatedQualifications.includes(id)) {
                                updatedQualifications.push(id);
                              } else {
                                const index = updatedQualifications.indexOf(id);
                                if (index > -1) {
                                  updatedQualifications.splice(index, 1);
                                }
                              }

                              formik.setFieldValue('qualificationIds', updatedQualifications);
                            }}
                            isDisabled={pending}
                          >
                            {qualification.label}
                          </CheckboxGroupInput>
                        );
                      })
                    : qualifications.map(qualification => {
                        return (
                          <CheckboxGroupInput
                            key={`qualification_${qualification.id}`}
                            name="qualificationIds"
                            value={qualification.id}
                            checked={false}
                            onChange={e => {
                              const { value } = e.target;
                              const id = Number(value);

                              const currentIds = Array.isArray(formik.values.qualificationIds)
                                ? formik.values.qualificationIds
                                : [];
                              const updatedQualifications = [...currentIds];

                              if (!updatedQualifications.includes(id)) {
                                updatedQualifications.push(id);
                              } else {
                                const index = updatedQualifications.indexOf(id);
                                if (index > -1) {
                                  updatedQualifications.splice(index, 1);
                                }
                              }

                              formik.setFieldValue('qualificationIds', updatedQualifications);
                            }}
                            isDisabled={pending}
                          >
                            {qualification.label}
                          </CheckboxGroupInput>
                        );
                      })}
                </FormSection>
              )}
            />
          </Stack>

          <FormActions mt={20}>
            <SecondaryButton type="button" onClick={() => history.goBack()}>
              Annuler
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={pending}>
              Enregistrer
            </PrimaryButton>
          </FormActions>
        </Form>
      )}
    </Formik>
  );
};
